import React from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { selectCurrentWorkspaceId } from "src/store/app";
import { OtaTagsDialog } from "src/ui/containers/dashboard/rollouts/components/VersionTable/OtaTagsDialog";
import { makeStyles } from "tss-react/mui";

import { showDetails } from "@dashboard/devices/store";
import { getOtaFileReleaseNotes, hasOtaFileReleaseNotes } from "@dashboard/rollouts/utils/otaFileReleaseNotes";
import { ChangesDialog } from "@dashboard/shared/components/ChangesDialog";
import { ConfirmDialog } from "@devices/components/shared";
import { Typography } from "@mui/material";
import { DangerOutlinedButton, PrimaryOutlinedButton, SecondaryButton } from "@shared/CustomButton";
import { MinimalTableCell } from "@shared/table/MinimalTableComponents";
import { showSuccessToast } from "@shared/toasts/Toasts";
import type { Row } from "@tanstack/react-table";

import { useDeleteOtaAnnotationMutation } from "../../api";
import { OtaFile } from "../../types";

const useStyles = makeStyles()((theme) => ({
    actionContainer: {
        display: "flex",
        gap: theme.spacing(1),
        justifyContent: "flex-end",
    },
}));

type Props = {
    row: Row<OtaFile>;
};

export const VersionTableActionsCell = ({ row }: Props) => {
    const { classes } = useStyles();
    const dispatch = useDispatch();
    const [isChangesDialogOpen, setIsChangesDialogOpen] = React.useState(false);
    const [isTagsDialogOpen, setIsTagsDialogOpen] = React.useState(false);
    const [isDeleteAnnotationDialogOpen, setIsDeleteAnnotationDialogOpen] = React.useState(false);
    const [deleteOtaAnnotation] = useDeleteOtaAnnotationMutation();
    const { t } = useTranslation(["pages", "modals", "common"]);
    const otaFile = row.original;
    const releaseNotes = getOtaFileReleaseNotes(otaFile);
    const hasReleaseNotes = hasOtaFileReleaseNotes(releaseNotes);
    const isDeleted = otaFile.status === "deleted";
    const isNoneChannel = otaFile.channel === "none";
    const hasOtaAnnotation = !!otaFile.annotation;
    const workspaceId = useSelector(selectCurrentWorkspaceId);

    const getTooltipTitle = () => {
        if (isDeleted) {
            return t("pages:rolloutsPage.tooltips.startRolloutDisabled");
        }
        if (isNoneChannel) {
            return t("pages:rolloutsPage.tooltips.startRolloutNoneChannel");
        }
        return t("pages:rolloutsPage.tooltips.startRollout");
    };

    const onDeleteOtaAnnotation = async () => {
        if (workspaceId !== null) {
            await deleteOtaAnnotation({
                codename: otaFile.codename,
                version: otaFile.version,
                workspaceId: workspaceId,
            }).unwrap();
            showSuccessToast(t("pages:rolloutsPage.toast.deleteOtaAnnotationSuccess"));
        }
    };

    const onStartRollout = () => {
        dispatch(
            showDetails({
                type: "create-rollout",
                selectedId: otaFile.buildId,
                codename: otaFile.codename,
                version: otaFile.version,
            }),
        );
    };

    return (
        <MinimalTableCell style={{ maxWidth: 150 }}>
            <div className={classes.actionContainer}>
                {hasOtaAnnotation && isDeleted ? (
                    <DangerOutlinedButton
                        tooltipProps={{ title: t("pages:rolloutsPage.tooltips.deleteOtaAnnotation") }}
                        onClick={() => setIsDeleteAnnotationDialogOpen(true)}
                    >
                        <i className="fa-solid fa-trash" />
                    </DangerOutlinedButton>
                ) : (
                    <PrimaryOutlinedButton
                        tooltipProps={{ title: getTooltipTitle() }}
                        onClick={onStartRollout}
                        disabled={isDeleted || isNoneChannel}
                    >
                        <i className="fa-solid fa-paper-plane" />
                    </PrimaryOutlinedButton>
                )}

                <SecondaryButton
                    tooltipProps={{ title: t("pages:rolloutsPage.tooltips.manageTags") }}
                    onClick={() => setIsTagsDialogOpen(true)}
                >
                    <i className="fa-solid fa-tags" />
                </SecondaryButton>

                <SecondaryButton
                    tooltipProps={{
                        title: hasReleaseNotes
                            ? t("pages:downloadsPage.tooltips.showChanges")
                            : t("pages:downloadsPage.tooltips.showChangesDisabled"),
                    }}
                    onClick={() => setIsChangesDialogOpen(true)}
                    disabled={!hasReleaseNotes}
                >
                    <i className="fas fa-list" />
                </SecondaryButton>
            </div>
            <ChangesDialog
                title={t("modals:changesDialog.titles.rollout")}
                onClose={() => setIsChangesDialogOpen(false)}
                changesWithDate={releaseNotes}
                open={isChangesDialogOpen}
            />
            <OtaTagsDialog
                open={isTagsDialogOpen}
                onClose={() => setIsTagsDialogOpen(false)}
                codename={otaFile.codename}
                version={otaFile.version}
                otaTags={otaFile.annotation}
            />
            <ConfirmDialog
                title={t("modals:deleteOtaAnnotation.title")}
                open={isDeleteAnnotationDialogOpen}
                dangerButton
                primaryActionText={t("common:button.delete")}
                onConfirm={onDeleteOtaAnnotation}
                onClose={() => setIsDeleteAnnotationDialogOpen(false)}
            >
                <Typography>{t("modals:deleteOtaAnnotation.description")}</Typography>
            </ConfirmDialog>
        </MinimalTableCell>
    );
};
